import DateTimePicker from 'common/components/DateTime/DateTimePicker';
import { Box, Grid } from '@material-ui/core';
import styles from './styles.scss';
import MultipleSelect from './MultiSelectWithCheckBox';
import { Fragment } from 'react';
import { isNil, get, isArray } from 'lodash';
import CompletedSignatures from './CompletedSignatures';
import AuditHistory from '../../AuditHistory/AuditHistory';
import { delegationDisclaimers } from './constants';
import DelegationStatement from 'components/common/DelegationStatemet';
import CustomizedMenus from 'components/common/CustomSelect';
import NewPISignatureForExistingDelegation from './NewPISignatures.jsx';
import { getDateNoTime } from 'common/util/dateTime';
import SelectDataSource from '../../../../components/SelectDataSource/index';

const PI_ROLE = 'Principal Investigator';

export default function DelegationSection(props) {
    const { data, isEditing, errors, numberOfSignatures, isDirtyForm } = props;

    function signatureStatement() {
        const usePluralForm = numberOfSignatures === 1 ? '' : 's';

        return delegationDisclaimers.signatures.hasSignatures.replace('__signatures__', numberOfSignatures).replace('__plural__', usePluralForm);
    }

    const isEditedButNoSignature = isDirtyForm && numberOfSignatures === 0;

    function delegationConditions() {
        if (!data || data.length === 0) return false;
        return {
            hasCompletedSignatures: data.completeSignatures.length > 0 || false,
            noCompletedSignatures: data.completeSignatures.length === 0 || false,
            hasEndDate: get(props.study, 'id', false) && props.hasChangeEndDate,
        };
    }

    function showCompletedSignatures() {
        if (!isEditing) {
            return false;
        }

        return isEditing && data.completeSignatures.length > 0;
    }

    return (
        <div className={styles.Wrapper}>
            <p className={styles.HeaderText}>Delegation</p>
            <Box className={styles.DateInputWrapper} component='div' >
                <Grid container spacing={3}>
                    <Grid item xs>
                        <span className={styles.InputLabel}>Start Date</span>
                        <DateTimePicker
                            autoFocus={false}
                            time={false}
                            noTimezone={true}
                            value={props.startDateSelected}
                            max={getDateNoTime(props.endDateSelected) || getDateNoTime(new Date(2099, 11, 31))}
                            onChange={props.onStarSelected}
                            currentDate={props.currentStartDate}
                            onCurrentDateChange={props.onCurrentStartDateChange}
                            className={styles.DatePicker}
                        />
                    </Grid>
                    <Grid item xs>
                        <span className={styles.InputLabel}>End Date</span>
                        <DateTimePicker
                            autoFocus={false}
                            time={false}
                            noTimezone={true}
                            value={props.endDateSelected}
                            min={getDateNoTime(props.startDateSelected)}
                            onChange={props.onEndDateSelected}
                            currentDate={props.currentEndDate}
                            onCurrentDateChange={props.onCurrentEndDateChange}
                            className={styles.DatePicker}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Fragment>
                {
                    (!props.hasDataSource && !isEditing) && (
                        <Fragment>
                            <SelectDataSource
                                label="Data Source"
                                placeholder="Data Source"
                                dataSources={props.dataSources}
                                isRequired={true}
                                buttonLabel={'Create'}
                                handleSelectedDataSource={props.handleSelectedDataSource}
                                handleCreateDataSource={props.handleCreateDataSource}
                                canAddDataSource={props.canAddDataSource}
                                errors={errors}
                                dataSourceError={errors.dataSource}
                                selectedDataSource={props.selectedDataSource}
                                delegationDisclaimers={delegationDisclaimers}
                            />
                            {errors.dataSource && (<p className={styles.errorMessage}>Data Source is required</p>)}
                        </Fragment>
                    )
                }
            </Fragment>
            <CustomizedMenus
                label="Role"
                placeholder="Select Role"
                options={props.roleOptions}
                isRequired={true}
                value={props.selectedRole ? props.selectedRole.label : ''}
                onSelectedOption={(selectedOption) => props.onRoleSelected(selectedOption)}
                isSearchable={false}
                notValid={errors.role || errors.tenureOverlaps}
            />
            {errors.role && (<p className={styles.errorMessage}>Role is required</p>)}
            {errors.tenureOverlaps && (<pre className={styles.errorMessage}>{delegationDisclaimers.PITenure.overlaps}</pre>)}
            <DelegationStatement type="warning" size="small" condition={!isEditing && props.selectedRole && props.selectedRole.label === PI_ROLE}>
                {delegationDisclaimers.RoleManagement.isPI}
            </DelegationStatement>
            <MultipleSelect
                handleChange={props.onTaskSelected}
                options={props.taskOptions}
                selectedValues={props.selectedTask}
                label='Responsibilities'
                placeHolder='Select Responsibilities'
                canSelectAll={true}
            />
            {
                (props.suggestedSignatures.length > 0) && (
                    <MultipleSelect
                        initialStamentCondition={isEditedButNoSignature && props.hasRequiredFields}
                        initialStatement={`${delegationDisclaimers.signatures.hasRequiredFields}`}
                        laterStamentCondition={props.numberOfSignatures > 0 && !props.isTheSameOldData}
                        laterStatement={`${signatureStatement()}`}
                        handleChange={props.onSignatureChange}
                        selectedValues={props.selectedSignatures}
                        label='Signature Requests'
                        selector='text'
                        placeHolder='Select Staff for Signatures'
                        options={props.suggestedSignatures}
                    />
                )
            }
            {showCompletedSignatures() && <CompletedSignatures completeSignatures={data.completeSignatures} />}
            <Box component='div' display='flex' flexDirection='column' paddingTop='10px'>
                <DelegationStatement type="warning" size="small" condition={delegationConditions() && delegationConditions().hasEndDate && !props.hasAdminRole}>
                    {delegationDisclaimers.AuditNote.hasEndDate}
                </DelegationStatement>
                <span className={styles.InputLabel}>
                    Audit Notes
                    {showCompletedSignatures() && (<span className={styles.Required}>{' '}* Required</span>)}
                </span>
                <textarea
                    className={styles.AuditNotes}
                    rows={4}
                    style={{ borderColor: `${props.isAuditNotesError ? '#C33' : '#b1b1b1'}` }}
                    onChange={(e) => props.onAuditNoteChange(e.target.value)}
                ></textarea>
                {props.isAuditNotesError && <p className={styles.auditNotesErrorLabel}>An audit note is required for any changes after signatures have been completed.</p>}
                <DelegationStatement type="warning" size="small" condition={delegationConditions() && delegationConditions().hasCompletedSignatures}>
                    {delegationDisclaimers.AuditNote.hasCompletedSignatures}
                </DelegationStatement>
                <DelegationStatement display={`${isEditing ? 'flex' : 'none'}`} type="warning" size="small" condition={delegationConditions() && delegationConditions().noCompletedSignatures}>
                    {delegationDisclaimers.AuditNote.noCompletedSignatures}
                </DelegationStatement>
            </Box>

            {
                (isArray(props.delegationRequiringNewPISignature) && props.delegationRequiringNewPISignature.length > 0) && (
                    <NewPISignatureForExistingDelegation
                        delegationRequiringNewPISignature={props.delegationRequiringNewPISignature}
                        onToggleNewPiSignatureForExistingDelegation={props.onToggleNewPiSignatureForExistingDelegation}
                        styles={styles}
                    />
                )
            }

            <Fragment>
                {
                    isEditing ? (
                        <Box component='div' flexDirection='column' paddingTop='10px'>
                            <AuditHistory
                                binderId={props.binderId || ''}
                                delegationId={data.id || ''}
                                delegationName={data.delegationName || ''}
                                isStudyType={!isNil(get(props.study, 'id')) || ''}
                                institutionId={props.institutionId || ''}
                                customStyling={{ paddingBottom: '100px' }}
                            />
                        </Box>
                    ) :
                        null
                }
            </Fragment>
        </div>
    );
}